import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API_BASE, apiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { CompanyLogo } from "@/components/CompanyLogo";
import { PoweredBy } from "@/components/PoweredBy";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PublicLoading, PublicError } from "@/pages/public/VisitorRegister";
import { Users, MapPin, Clock, DoorOpen, CalendarClock, ChevronRight } from "lucide-react";

const LIVE = {
  AVAILABLE: { label: "Available now", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", dot: "bg-emerald-500" },
  BOOKED: { label: "Booked now", cls: "bg-rose-50 text-rose-700 border-rose-200", dot: "bg-rose-500" },
  STARTING_SOON: { label: "Starting soon", cls: "bg-amber-50 text-amber-700 border-amber-200", dot: "bg-amber-500" },
};

function LiveBadge({ status, code }) {
  const s = LIVE[status];
  if (!s) return null;
  return (
    <span data-testid={`branch-room-live-${code}`} className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border ${s.cls}`}>
      <span className="relative flex h-2 w-2">
        <span className={`absolute inline-flex h-full w-full rounded-full opacity-60 animate-ping ${s.dot}`} />
        <span className={`relative inline-flex rounded-full h-2 w-2 ${s.dot}`} />
      </span>
      {s.label}
    </span>
  );
}

export default function BranchBooking() {
  const { publicId } = useParams();
  const navigate = useNavigate();
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  const load = useCallback((d) => {
    axios.get(`${API_BASE}/public/branch/${publicId}/rooms?date=${d}`)
      .then((r) => setData(r.data)).catch((e) => setErr(apiError(e)));
  }, [publicId]);

  useEffect(() => { load(date); }, [date, load]);
  useEffect(() => {
    const t = setInterval(() => load(date), 30000);
    return () => clearInterval(t);
  }, [date, load]);

  if (err) return <PublicError title="Booking Unavailable" message={err} />;
  if (!data) return <PublicLoading />;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="branch-booking-page">
      <div className="sf-grid-bg px-6 py-8 text-white"><Logo className="h-7" light /></div>
      <div className="max-w-3xl mx-auto px-4 -mt-6 pb-12">
        <div className="bg-white rounded-3xl shadow-xl border border-slate-200 p-6 sm:p-8 sf-fade-up">
          {data.logo_url && <CompanyLogo logoUrl={data.logo_url} className="h-9 mb-4" alt={data.company} />}
          <p className="text-xs font-semibold uppercase tracking-wider text-blue-600 flex items-center gap-1"><MapPin size={14} /> {data.company}</p>
          <h1 className="font-display text-3xl font-bold text-navy-900 mt-1">{data.branch}</h1>
          {data.address && <p className="text-sm text-slate-500 mt-1">{data.address}</p>}

          <div className="flex items-center justify-between mt-6 mb-3">
            <h3 className="font-display font-semibold text-navy-900">Meeting Rooms</h3>
            <Input type="date" value={date} min={today} onChange={(e) => setDate(e.target.value)} className="h-9 w-40" data-testid="branch-booking-date" />
          </div>

          {data.rooms.length === 0 ? (
            <div className="text-center py-14 text-slate-400">
              <DoorOpen size={38} className="mx-auto mb-3 opacity-60" />
              <p className="text-sm">No meeting rooms available at this branch.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {data.rooms.map((r) => (
                <button
                  key={r.public_id}
                  onClick={() => navigate(`/book/room/${r.public_id}`)}
                  data-testid={`branch-room-${r.code}`}
                  className="w-full text-left bg-white rounded-2xl border border-slate-200 p-5 hover:border-navy-900 hover:shadow-md transition-all group"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-display font-bold text-navy-900 text-lg">{r.room_name}</h4>
                        <LiveBadge status={r.live_status} code={r.code} />
                      </div>
                      <p className="text-xs text-slate-400 font-mono">{r.code}{r.floor ? ` · ${r.floor}` : ""}</p>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        <span className="text-xs text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full flex items-center gap-1"><Users size={11} /> {r.capacity}</span>
                        {(r.facilities || []).map((f) => <span key={f} className="text-xs text-navy-800 bg-navy-50 px-2 py-0.5 rounded-full">{f}</span>)}
                        <span className="text-xs text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full flex items-center gap-1"><Clock size={11} /> Free {r.free_duration_minutes}m</span>
                      </div>
                    </div>
                    <ChevronRight size={20} className="text-slate-300 group-hover:text-navy-900 shrink-0 mt-1 transition-colors" />
                  </div>

                  <div className="mt-3 pt-3 border-t border-slate-100">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5">Booked on {date}</p>
                    {r.bookings.length === 0 ? (
                      <p className="text-sm text-emerald-600 flex items-center gap-1"><CalendarClock size={13} /> Fully available all day</p>
                    ) : (
                      <div className="flex flex-wrap gap-1.5" data-testid={`branch-room-slots-${r.code}`}>
                        {r.bookings.map((b, i) => (
                          <span key={i} className={`text-xs font-medium px-2 py-1 rounded-lg border ${b.status === "PENDING_APPROVAL" ? "bg-amber-50 border-amber-200 text-amber-700" : "bg-navy-50 border-navy-100 text-navy-800"}`}>
                            {b.start_time}–{b.end_time}{b.status === "PENDING_APPROVAL" ? " (pending)" : ""}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="mt-4">
                    <span className="inline-flex items-center gap-1 text-sm font-semibold text-navy-900 group-hover:gap-2 transition-all">Select & Book <ChevronRight size={15} /></span>
                  </div>
                </button>
              ))}
            </div>
          )}
          <div className="mt-6 pt-4 border-t border-slate-100 flex justify-center"><PoweredBy /></div>
        </div>
      </div>
    </div>
  );
}
